<template>
  <div>
    <!-- Target Type Selector -->
    <div class="form-group mb-3">
      <label class="form-label fw-semibold">Select Target Recipient</label>
      <div class="d-flex gap-2 flex-wrap">
        <o-button
          v-for="opt in targetOptions"
          :key="opt.value"
          type="button"
          class="btn-custom"
          :variant="'none'"
          :class="targetType === opt.value ? 'btn-custom-primary' : 'btn-custom-outline'"
          @click="selectTarget(opt.value)"
        >
          {{ opt.label }}
        </o-button>
      </div>
    </div>

    </div>

    <!-- Tier Selection (Multi-select like Newsletter pattern) -->
    <div class="form-group mb-3" v-if="targetType === 'tier' || targetType === 'all'">
      <label class="form-label">Target Tiers</label>
      <div class="tier-selection">
        <div class="form-check mb-2">
          <input
            class="form-check-input"
            type="checkbox"
            id="allTiers"
            v-model="allTiersSelected"
            @change="toggleAllTiers"
          >
          <label class="form-check-label fw-medium" for="allTiers">
            All Member Tiers<template v-if="allMemberCount > 0"> ({{ allMemberCount }} Member{{ allMemberCount !== 1 ? 's' : '' }})</template>
          </label>
        </div>
        <div class="tier-list" v-if="!allTiersSelected">
          <p class="text-secondary opacity-80 small fw-medium mb-2 mt-1">
            Select specific tiers:
          </p>
          <div v-if="tiers.length === 0" class="text-muted small p-2">
            <span class="spinner-border spinner-border-sm me-1"></span> Loading tiers...
          </div>
          <div
            v-else
            class="form-check"
            v-for="tier in tiers"
            :key="tier.id"
          >
            <input
              class="form-check-input"
              type="checkbox"
              :id="`tier-${tier.id}`"
              :value="tier.id"
              v-model="selectedTierIds"
              @change="onTiersChange"
            >
            <label class="form-check-label" :for="`tier-${tier.id}`">
              {{ tier.name }} ({{ tier.member_tier_count }} Member{{ tier.member_tier_count !== 1 ? 's' : '' }})
            </label>
          </div>
        </div>
      </div>
    </div>

    <!-- ===== Member Selection Area ===== -->
    <div class="form-group mb-2" v-if="targetType === 'selected'">
      <label class="form-label">Search &amp; Select Member</label>

      <!-- Mode toggle buttons: Search | Scan -->
      <div class="mode-toggle-row mb-2">
        <o-button
          type="button"
          class="btn-custom"
          :variant="'none'"
          :class="selectMode === 'search' ? 'btn-custom-primary' : 'btn-custom-outline'"
          @click="setMode('search')"
        >
          <i class="fas fa-search me-1"></i> Search
        </o-button>
        <o-button
          type="button"
          class="btn-custom"
          :variant="'none'"
          :class="selectMode === 'scan' ? 'btn-custom-primary-outline' : 'btn-custom-outline'"
          @click="setMode('scan')"
        >
          <i class="fas fa-qrcode me-1"></i> Scan QR
        </o-button>
      </div>

      <!-- ── SEARCH MODE ── -->
      <template v-if="selectMode === 'search'">
        <div class="modern-search-wrapper mb-2">
          <span class="modern-search-icon"><i class="fas fa-search"></i></span>
          <input
            type="text"
            class="form-control modern-search-input"
            placeholder="Search members..."
            v-model="searchQuery"
            aria-label="Search members"
            autocomplete="off"
          />
          <button
            v-if="searchQuery"
            class="modern-search-clear"
            type="button"
            @click="searchQuery = ''"
            aria-label="Clear search"
          >
            <i class="fas fa-times"></i>
          </button>
        </div>

        <div class="members-inline-list members-list" ref="listRef" @scroll="handleScroll">
          <div v-if="!listLoaded" class="text-center p-3">
            Loading members...
          </div>
          <div v-else-if="filteredMembers.length === 0" class="text-center p-3">
            No members found
          </div>
          <div
            v-else
            v-for="m in filteredMembers"
            :key="m.id"
            class="member-item p-2 border-bottom cursor-pointer hover-bg-gray-100"
            :class="{ 'bg-light': selectedIds.includes(m.id) }"
            @click="toggleMember(m)"
          >
            <div class="d-flex justify-content-between align-items-center">
              <div>
                <div class="fw-bold" style="font-size: 0.9rem;">{{ m.full_name }}</div>
                <div class="text-muted small">{{ m.email }}</div>
              </div>
              <div v-if="selectedIds.includes(m.id)" class="text-primary-custom fs-5">
                <span>✓</span>
              </div>
            </div>
          </div>
          <div v-if="loadingMore" class="text-center text-muted small p-2">
            Loading more...
          </div>
        </div>
      </template>

      <!-- ── SCAN MODE ── -->
      <template v-else-if="selectMode === 'scan'">
        <div class="scan-area">
          <div id="gift-scan-wrapper" class="scan-wrapper"></div>
          <div v-if="scanMessage" class="scan-message" :class="scanSuccess ? 'text-primary-custom' : 'text-danger'">
            <i :class="scanSuccess ? 'fas fa-check-circle' : 'fas fa-exclamation-circle'" class="me-1"></i>
            {{ scanMessage }}
          </div>
          <p class="text-muted small text-center mt-2 mb-0">
            <i class="fas fa-info-circle me-1"></i>Point the camera at member's QR Code
          </p>
        </div>
      </template>

      <!-- Selected chips -->
      <div v-if="selectedMembers.length > 0" class="selected-chips mt-2">
        <span v-for="m in selectedMembers" :key="m.id" class="chip">
          {{ m.full_name }}
          <button type="button" @click="removeMember(m.id)" class="chip-remove">✕</button>
        </span>
      </div>
      <small class="text-muted mt-1 d-block">{{ selectedMembers.length }} members selected</small>
    </div>
</template>

<script>
import { ref, computed, watch, onBeforeUnmount, nextTick } from 'vue'
import { useStore } from 'vuex'
import { Html5Qrcode } from 'html5-qrcode'
import { OButton } from '@/components/OmnihControl'

export default {
  name: 'GiftVoucherTargetSelector',
  components: { OButton },
  props: {
    targetType:  { type: String, default: 'tier' },
    tierIds:     { type: Array, default: () => [] },
    isAllTiers:  { type: Boolean, default: true },
    memberIds:   { type: Array, default: () => [] },
    tiers:       { type: Array, default: () => [] },
    corporateId: { type: [String, Number], default: null },
    allMemberCount: { type: Number, default: 0 },
  },
  emits: ['update:targetType', 'update:tierIds', 'update:memberIds', 'update:isAllTiers', 'change'],

  setup(props, { emit }) {
    const store = useStore()

    const targetOptions = [
      { value: 'tier',     label: 'By Tier'       },
      { value: 'selected', label: 'Select Member'  },
    ]

    /* ---- tier ---- */
    const selectedTierIds = ref([...props.tierIds])
    const allTiersSelected = ref(props.isAllTiers)

    function onTiersChange() {
      emit('update:tierIds', [...selectedTierIds.value])
      emit('change')
    }

    function toggleAllTiers() {
      emit('update:isAllTiers', allTiersSelected.value)
      if (allTiersSelected.value) {
        selectedTierIds.value = []
        emit('update:tierIds', [])
      }
      emit('change')
    }

    // Auto-select "All Tiers" if all specific tiers are checked manually
    watch(selectedTierIds, (newIds) => {
      if (!allTiersSelected.value && props.tiers.length > 0 && newIds.length === props.tiers.length) {
        allTiersSelected.value = true
        emit('update:isAllTiers', true)
        selectedTierIds.value = []
        emit('update:tierIds', [])
      }
    }, { deep: true })

    watch(() => props.isAllTiers, val => { allTiersSelected.value = val })

    /* ---- mode: search | scan ---- */
    const selectMode = ref('search')

    /* ---- scanner state ---- */
    let scanner = null
    let cameraId = ''
    const scanMessage = ref('')
    const scanSuccess = ref(false)

    Html5Qrcode.getCameras()
      .then(devices => { if (devices?.length) cameraId = devices[0].id })
      .catch(() => {})

    async function startScanner() {
      await nextTick()
      try {
        scanner = new Html5Qrcode('gift-scan-wrapper')
        const el = document.getElementById('gift-scan-wrapper')
        const size = el ? Math.min(el.clientWidth, 280) : 250
        await scanner.start(
          { deviceId: { exact: cameraId } },
          { fps: 6, qrbox: { width: size, height: size } },
          onScanSuccess
        )
      } catch (e) {
        console.error('Scanner start failed', e)
        scanMessage.value = 'Camera not available'
        scanSuccess.value = false
      }
    }

    function stopScanner() {
      try { if (scanner?.stop) scanner.stop() } catch (e) { /* ignore */ }
      scanner = null
    }

    async function onScanSuccess(decodedText) {
      let memberData
      try {
        memberData = JSON.parse(decodedText)
      } catch {
        showScanMsg('Invalid QR', false)
        return
      }

      const memberId = memberData.memberId
      let raw = store.state.Member.members.find(v => v.id == memberId)

      if (!raw) {
        try {
          raw = await store.dispatch('Member/read_single', memberId)
          if (raw) store.commit('Member/add_single_member', { member: raw })
        } catch { raw = null }
      }

      if (!raw) { showScanMsg('Member not found', false); return }

      const mapped = mapMember(raw)
      if (!selectedIds.value.includes(mapped.id)) {
        selectedIds.value.push(mapped.id)
        selectedMembers.value.push(mapped)
        emit('update:memberIds', [...selectedIds.value])
        emit('change')
      }
      showScanMsg(`${mapped.full_name} added ✓`, true)
    }

    function showScanMsg(msg, ok) {
      scanMessage.value = msg
      scanSuccess.value = ok
      setTimeout(() => { scanMessage.value = '' }, 2500)
    }

    async function setMode(mode) {
      if (mode === selectMode.value) return
      stopScanner()
      selectMode.value = mode
      if (mode === 'scan') {
        await startScanner()
      }
    }

    /* ---- selected member state ---- */
    const selectedMembers = ref([])
    const selectedIds     = ref([...props.memberIds])

    /* ---- list state ---- */
    const listLoaded  = ref(false)
    const loadingMore = ref(false)
    const allLoaded   = ref(false)
    const searchQuery = ref('')
    const allMembers  = ref([])
    const listRef     = ref(null)

    function mapMember(m) {
      return {
        id:        m.id,
        full_name: `${m.title || ''} ${m.first_name || ''} ${m.last_name || ''}`.replace(/\s+/g, ' ').trim(),
        email:     m.user?.email || m.email || '',
      }
    }

    const filteredMembers = computed(() => {
      const q = searchQuery.value.toLowerCase().trim()
      if (!q) return allMembers.value
      return allMembers.value.filter(
        m => m.full_name.toLowerCase().includes(q) || m.email.toLowerCase().includes(q)
      )
    })

    async function loadMembers() {
      listLoaded.value = false
      allMembers.value = []
      allLoaded.value = false
      store.commit('Member/reset_meta')
      if (props.corporateId) {
        store.commit('Member/add_filter', { filter: { corporate_id: props.corporateId } })
      }
      try {
        await store.dispatch('Member/read')
        allMembers.value = (store.state.Member.members || []).map(mapMember)
      } catch (e) {
        console.error('Failed to load members', e)
      } finally {
        listLoaded.value = true
      }
    }

    // debounce search so typing triggers server-side search to get all members
    let searchDebounce = null;
    watch(searchQuery, (val) => {
      if (searchDebounce) clearTimeout(searchDebounce);
      searchDebounce = setTimeout(async () => {
        const query = (val || '').trim();
        
        if (query === '') {
          store.commit('Member/clear_search');
          await loadMembers();
          return;
        }

        try {
          listLoaded.value = false;
          // dispatch Member/all to fetch all members associated with corporate, bypassing pagination
          await store.dispatch('Member/all', props.corporateId);
          
          allMembers.value = (store.state.Member.members || []).map(mapMember);
          listLoaded.value = true;
        } catch (e) {
          console.error('member search failed', e);
          await loadMembers(); // fallback
        }
      }, 350);
    });

    async function handleScroll() {
      const el = listRef.value
      if (!el || loadingMore.value || !listLoaded.value) return
      // if searching, all are loaded, so disable infinite scroll logic
      if (searchQuery.value.trim() !== '') return

      if (el.scrollHeight - el.scrollTop - el.clientHeight < 150) {
        const meta       = store.state.Member.meta || {}
        const currentPage = meta.page || 1
        const limit      = meta.limit || 10
        const total      = meta.total || 1
        const totalPages = meta.total_pages || (total > limit ? Math.ceil(total / limit) : total)
        
        if (currentPage < totalPages) {
          loadingMore.value = true;
          const nextMeta = { ...meta, page: currentPage + 1 };
          store.commit('Member/switch_page', { meta: nextMeta });
          
          try {
            await store.dispatch('Member/read');
            // append to existing
            const newMembers = (store.state.Member.members || []).map(mapMember);
            // push only unique members
            const existingIds = new Set(allMembers.value.map(m => m.id));
            newMembers.forEach(m => {
              if (!existingIds.has(m.id)) {
                allMembers.value.push(m);
              }
            });
          } catch (e) { 
            console.error('Failed to load more members', e);
          } finally { 
            loadingMore.value = false;
          }
        } else {
          if (!allLoaded.value) {
            loadingMore.value = true;
            try {
              await store.dispatch('Member/all', props.corporateId);
              allMembers.value = (store.state.Member.members || []).map(mapMember);
              allLoaded.value = true;
            } catch (e) {
              console.error('Failed to load all members on scroll end', e);
            } finally {
              loadingMore.value = false;
            }
          }
        }
      }
    }

    function toggleMember(m) {
      if (selectedIds.value.includes(m.id)) {
        removeMember(m.id)
      } else {
        selectedIds.value.push(m.id)
        selectedMembers.value.push(m)
        emit('update:memberIds', [...selectedIds.value])
        emit('change')
      }
    }

    function removeMember(id) {
      selectedIds.value    = selectedIds.value.filter(i => i !== id)
      selectedMembers.value = selectedMembers.value.filter(m => m.id !== id)
      emit('update:memberIds', [...selectedIds.value])
      emit('change')
    }

    function selectTarget(value) {
      stopScanner()
      emit('update:targetType', value)
      emit('update:tierIds', [])
      emit('update:memberIds', [])
      emit('update:isAllTiers', true)
      selectedTierIds.value = []
      allTiersSelected.value = true
      selectedMembers.value = []
      selectedIds.value     = []
      searchQuery.value     = ''
      allMembers.value      = []
      listLoaded.value      = false
      selectMode.value      = 'search'
      emit('change')
      if (value === 'selected') loadMembers()
    }

    watch(() => props.tierIds, val => { selectedTierIds.value = [...(val || [])] })

    watch(() => props.memberIds, val => { selectedIds.value = [...(val || [])] })

    watch(() => props.corporateId, val => {
      if (val && props.targetType === 'selected' && !listLoaded.value) loadMembers()
    })

    onBeforeUnmount(() => {
        stopScanner();
    })

    return {
      targetOptions, onTiersChange, toggleAllTiers,
      selectedTierIds, allTiersSelected,
      selectMode, setMode,
      scanMessage, scanSuccess,
      selectedMembers, selectedIds,
      listLoaded, loadingMore, searchQuery,
      filteredMembers, listRef,
      toggleMember, removeMember,
      selectTarget, handleScroll,
    }
  }
}
</script>

<style scoped>
/* mode toggle */
.mode-toggle-row { display: flex; gap: 8px; }

/* inline list */
.members-inline-list {
  border: 1px solid #dee2e6;
  border-radius: 6px;
  max-height: 220px;
  overflow-y: auto;
  background: #fff;
}

.cursor-pointer { cursor: pointer; }
.hover-bg-gray-100:hover { background-color: #f8f9fa !important; }

/* scan area */
.scan-area { display: flex; flex-direction: column; align-items: center; }
.scan-wrapper {
  width: 100%;
  max-width: 360px;
  border-radius: 8px;
  background: #111;
}
.scan-message {
  margin-top: 8px;
  font-size: 0.875rem;
  font-weight: 500;
  text-align: center;
}

/* chips */
.selected-chips { display: flex; flex-wrap: wrap; gap: 4px; }
.chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: var(--primary-color, #F93E3E);
  color: #fff;
  padding: 4px 10px;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 500;
}
.chip-remove {
  background: transparent;
  border: none;
  color: #fff;
  cursor: pointer;
  padding: 0;
  font-size: 0.7rem;
  line-height: 1;
  margin-left: 2px;
}

/* Custom Buttons Styling from TransactionForm pattern */
.btn-custom {
    font-weight: 500;
    font-size: 13px;
    height: 36px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 8px;
    padding: 0 16px;
    transition: all 0.2s ease;
    border: 1px solid transparent !important;
    cursor: pointer;
    box-shadow: none !important;
}

.btn-custom-primary {
    background-color: var(--primary-color, #F93E3E) !important;
    border-color: var(--primary-color, #F93E3E) !important;
    color: #ffffff !important;
    box-shadow: 0 2px 6px rgba(0,0,0,0.06) !important;
}

.btn-custom-primary:hover {
    opacity: 0.95;
    /* Kita hilangkan filter brigtness kaku dan ganti ke opacity halus */
}

.btn-custom-outline {
    background-color: #ffffff !important;
    border-color: #e2e8f0 !important;
    color: #64748b !important;
}

.btn-custom-outline:hover {
    background-color: #f8fafc !important;
    border-color: var(--primary-color, #F93E3E) !important;
    color: var(--primary-color, #F93E3E) !important;
}

.btn-custom-primary-outline {
    background-color: #ffffff !important;
    border-color: var(--primary-color, #F93E3E) !important;
    color: var(--primary-color, #F93E3E) !important;
    box-shadow: 0 2px 6px rgba(0,0,0,0.05) !important;
}

.btn-custom-primary-outline:hover {
    opacity: 0.9;
}

/* Tier Selection Styling from NewsletterForm pattern */
.tier-selection {
    background: #f9fafb;
    border: 1px solid #e5e7eb;
    border-radius: 12px;
    padding: 1.25rem;
}

.tier-list {
    margin-left: 1.5rem;
    padding-top: 1rem;
    border-top: 1px solid #e5e7eb;
    margin-top: 1rem;
}

.text-secondary.opacity-80 {
    opacity: 0.8;
    color: var(--primary-color, #F93E3E) !important;
}

.form-check-input:focus,
.form-control:focus {
    border-color: var(--primary-color, #F93E3E) !important;
    box-shadow: 0 0 0 0.25rem var(--secondary-color, #FEE9E9) !important;
}

.form-check-input:checked {
    background-color: var(--primary-color, #F93E3E) !important;
    border-color: var(--primary-color, #F93E3E) !important;
}

.text-primary-custom {
    color: var(--primary-color, #F93E3E) !important;
}

/* Modern Search Input */
.modern-search-wrapper {
    position: relative;
    display: flex;
    align-items: center;
    width: 100%;
}

.modern-search-icon {
    position: absolute;
    left: 12px;
    color: #94a3b8;
    font-size: 14px;
    pointer-events: none;
}

.modern-search-input {
    padding-left: 36px !important;
    padding-right: 36px !important;
    height: 40px;
    border-radius: 10px !important;
    border: 1px solid #e2e8f0 !important;
    background-color: #f8fafc;
    font-size: 13px;
    box-shadow: none !important;
    transition: all 0.2s ease;
}

.modern-search-input:focus {
    background-color: #ffffff;
    border-color: var(--primary-color, #F93E3E) !important;
    box-shadow: 0 0 0 0.25rem var(--secondary-color, #FEE9E9) !important;
}

.modern-search-clear {
    position: absolute;
    right: 8px;
    background: transparent;
    border: none;
    color: #94a3b8;
    width: 24px;
    height: 24px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.2s;
    font-size: 12px;
}

.modern-search-clear:hover {
    background-color: #e2e8f0;
    color: #475569;
}
</style>
